import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaCalculator, FaTag, FaCalendarAlt, FaMoneyBillWave, FaChartBar, FaLightbulb, FaQuestion, FaTruck, FaMoneyCheck } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const BlackFridayCalculator = () => {
  const [formData, setFormData] = useState({
    originalPrice: '',
    discountPercentage: '',
    additionalDiscount: '',
    salesTax: '',
    shippingCost: '',
    cashBack: '',
  });

  const [results, setResults] = useState({
    finalPrice: 0,
    totalSavings: 0,
    effectiveDiscount: 0,
    roi: 0,
  });

  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const calculateBlackFriday = (e) => {
    e.preventDefault();
    setError('');

    try {
      const {
        originalPrice,
        discountPercentage,
        additionalDiscount,
        salesTax,
        shippingCost,
        cashBack,
      } = formData;

      if (!originalPrice || !discountPercentage) {
        throw new Error('Please enter both original price and discount percentage');
      }

      const priceAfterFirstDiscount = originalPrice * (1 - discountPercentage / 100);
      const priceAfterAdditionalDiscount = priceAfterFirstDiscount * (1 - additionalDiscount / 100);
      const priceWithTax = priceAfterAdditionalDiscount * (1 + salesTax / 100);
      const finalPrice = priceWithTax + parseFloat(shippingCost) - parseFloat(cashBack);

      const totalSavings = originalPrice - finalPrice;
      const effectiveDiscount = ((originalPrice - finalPrice) / originalPrice) * 100;
      const roi = (totalSavings / originalPrice) * 100;

      setResults({
        finalPrice: finalPrice.toFixed(2),
        totalSavings: totalSavings.toFixed(2),
        effectiveDiscount: effectiveDiscount.toFixed(2),
        roi: roi.toFixed(2),
      });

      // Update chart data
      setChartData({
        options: {
          chart: {
            type: 'bar',
            height: 350,
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              horizontal: true,
            },
          },
          xaxis: {
            categories: ['Original Price', 'Final Price', 'Savings'],
          },
          theme: {
            mode: 'light',
          },
        },
        series: [{
          name: 'Amount ($)',
          data: [parseFloat(originalPrice), finalPrice, totalSavings],
        }],
      });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
      <Helmet>
        <title>Black Friday Calculator</title>
        <meta name="description" content="Calculate your Black Friday savings and final price with our calculator." />
        <meta property="og:title" content="Black Friday Calculator" />
        <meta property="og:description" content="Calculate your Black Friday savings and final price with our calculator." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Black Friday Calculator" />
        <meta name="twitter:description" content="Calculate your Black Friday savings and final price with our calculator." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <FaCalculator className="text-4xl text-indigo-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Black Friday Calculator</h1>
          <p className="text-gray-600">Calculate your Black Friday savings and final price</p>
        </div>

        <div className="calculator-content bg-white rounded-lg shadow-md p-6">
          <div className="calculator-form-section">
            <form onSubmit={calculateBlackFriday} className="space-y-6">
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaMoneyBillWave className="mr-2 text-indigo-500" /> Original Price
                </label>
                <input
                  type="number"
                  id="originalPrice"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  placeholder="Enter original price"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">The original price of the item before any discounts</span>
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaTag className="mr-2 text-indigo-500" /> Discount Percentage
                </label>
                <input
                  type="number"
                  id="discountPercentage"
                  name="discountPercentage"
                  value={formData.discountPercentage}
                  onChange={handleChange}
                  placeholder="Enter discount percentage"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">The initial Black Friday discount percentage</span>
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaTag className="mr-2 text-indigo-500" /> Additional Discount
                </label>
                <input
                  type="number"
                  id="additionalDiscount"
                  name="additionalDiscount"
                  value={formData.additionalDiscount}
                  onChange={handleChange}
                  placeholder="Enter additional discount percentage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">Any additional discounts or coupons</span>
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaCalendarAlt className="mr-2 text-indigo-500" /> Sales Tax (%)
                </label>
                <input
                  type="number"
                  id="salesTax"
                  name="salesTax"
                  value={formData.salesTax}
                  onChange={handleChange}
                  placeholder="Enter sales tax percentage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">Local sales tax percentage</span>
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaTruck className="mr-2 text-indigo-500" /> Shipping Cost
                </label>
                <input
                  type="number"
                  id="shippingCost"
                  name="shippingCost"
                  value={formData.shippingCost}
                  onChange={handleChange}
                  placeholder="Enter shipping cost"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">Any shipping or delivery costs</span>
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaMoneyCheck className="mr-2 text-indigo-500" /> Cash Back
                </label>
                <input
                  type="number"
                  id="cashBack"
                  name="cashBack"
                  value={formData.cashBack}
                  onChange={handleChange}
                  placeholder="Enter cash back amount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-500 mt-1">Any cash back or rewards you'll receive</span>
              </div>

              <button
                type="submit"
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                Calculate Black Friday Deal
              </button>

              {error && (
                <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
                  {error}
                </div>
              )}
            </form>
          </div>

          {results.finalPrice > 0 && (
            <div className="mt-8">
              <div className="results-header flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Results</h2>
                <FaChartBar className="text-3xl text-indigo-600" />
              </div>

              <div className="results-chart mb-8">
                <Chart options={chartData?.options} series={chartData?.series} type="bar" height={350} />
              </div>

              <div className="results-details grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <FaMoneyBillWave className="text-2xl text-indigo-600 mr-4" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Final Price</h3>
                      <p className="text-2xl font-bold text-indigo-600">${results.finalPrice}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <FaTag className="text-2xl text-indigo-600 mr-4" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Total Savings</h3>
                      <p className="text-2xl font-bold text-indigo-600">${results.totalSavings}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-indigo-600 mr-4" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Effective Discount</h3>
                      <p className="text-2xl font-bold text-indigo-600">{results.effectiveDiscount}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <FaChartBar className="text-2xl text-indigo-600 mr-4" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">ROI</h3>
                      <p className="text-2xl font-bold text-indigo-600">{results.roi}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12">
          <div className="calculator-features bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Key Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaLightbulb className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">Comprehensive Calculations</h3>
                </div>
                <p className="text-gray-600">Calculate final price including all discounts, taxes, and shipping.</p>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaChartBar className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">Visual Insights</h3>
                </div>
                <p className="text-gray-600">See your savings breakdown with our interactive bar chart.</p>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaCalculator className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">Advanced Features</h3>
                </div>
                <p className="text-gray-600">Includes cash back, additional discounts, and ROI calculations.</p>
              </div>
            </div>
          </div>

          <div className="calculator-usage bg-white rounded-lg shadow-md p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">How to Use</h2>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">1</span>
                <span>Enter the original price of the item</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">2</span>
                <span>Input the Black Friday discount percentage</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">3</span>
                <span>Add any additional discounts or coupons</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">4</span>
                <span>Enter the sales tax percentage</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">5</span>
                <span>Add shipping costs if applicable</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">6</span>
                <span>Include any cash back or rewards</span>
              </li>
              <li className="flex items-start">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-600 text-white mr-3">7</span>
                <span>Click "Calculate Black Friday Deal" to see your results</span>
              </li>
            </ol>
          </div>

          <div className="calculator-faq bg-white rounded-lg shadow-md p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaQuestion className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">What is Black Friday?</h3>
                </div>
                <p className="text-gray-600">Black Friday is a major shopping event that typically occurs the day after Thanksgiving in the United States.</p>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaQuestion className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">How do additional discounts work?</h3>
                </div>
                <p className="text-gray-600">Additional discounts are applied after the initial Black Friday discount, providing extra savings.</p>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <FaQuestion className="text-3xl text-indigo-600 mr-4" />
                  <h3 className="text-lg font-semibold text-gray-900">What is ROI in this context?</h3>
                </div>
                <p className="text-gray-600">ROI shows the percentage of savings relative to the original price, helping you understand the deal's value.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlackFridayCalculator;
